import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';

import InfoIcon from '@/assets/svg/ui-elements/info.svg';
import AddIcon from '@/assets/svg/ui-elements/plus.svg';
import TrashIcon from '@/assets/svg/ui-elements/trash-2.svg';
import {
  Alert,
  Button,
  IconButton,
  Input,
  Select,
  type SelectOption,
  Separator,
  theme,
  Tooltip,
  Typography,
} from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { formatWithoutFraction } from '@/utils/numbers/formatters';

import {
  AllocationSummary,
  ChangedOutline,
  ShareClassCard,
  ShareClassFields,
} from './share-structure.styled';
import { useDeferredRemove } from './use-deferred-remove';
import { useStableKeys } from './use-stable-keys';

export type TemplateShareholderBase = {
  type: string | null;
  email: string | null;
  fullName: string | null;
  numberOfShares: number | null;
  [key: string]: unknown;
};

export type TemplateShareClassBase = {
  class: string | null;
  numberOfShares: number | null;
  shareholders: TemplateShareholderBase[] | null;
  [key: string]: unknown;
};

type Props<T extends TemplateShareClassBase> = {
  shareClasses: T[];
  selectClassOptions: readonly SelectOption[];
  fieldErrors: Record<string, unknown>;
  isStepValidated: boolean;
  onClassChange: (_index: number, _value: string) => void;
  onTotalSharesChange: (_index: number, _raw: string) => void;
  onRemoveShareClass: (_index: number) => void;
  onAddShareClass: () => void;
  canAddMore?: boolean;
  disableRemove?: boolean;
  disabled?: boolean;
  renderShareholders?: (_el: T, _index: number) => ReactNode;
  /** Renders the add button into this element instead of below the cards. */
  addButtonSlot?: HTMLElement | null;
  getClassFieldKey?: (_index: number) => string;
  getTotalSharesFieldKey?: (_index: number) => string;
  changedColor?: string;
  getIsClassChanged?: (_index: number) => boolean;
  getIsTotalSharesChanged?: (_index: number) => boolean;
};

const TOTAL_SHARES_TOOLTIP =
  'Enter the total number of shares for this class. Then expand the class to list all voting shareholders who own 10% or more.';

const AGGREGATED_TOOLTIP =
  'These totals are calculated automatically from the information you entered above. They’re provided to help you review and confirm that everything looks correct.';

const UNDER_ALLOCATED_TITLE = 'The shares you entered for this class don’t add up to the total.';

const UNDER_ALLOCATED_DETAIL =
  'This may be because some shareholders own less than 10% (and don’t need to be listed). Please double-check to ensure your entries are correct.';

const OVER_ALLOCATED =
  'Error: The total shares entered for this class exceed 100%. Please review and correct the shareholder entries.';

const ICON_SIZE = 16;

export const ShareStructureTemplate = <T extends TemplateShareClassBase>({
  shareClasses,
  selectClassOptions,
  fieldErrors,
  isStepValidated,
  onClassChange,
  onTotalSharesChange,
  onRemoveShareClass,
  onAddShareClass,
  canAddMore = true,
  disableRemove = false,
  disabled = false,
  renderShareholders,
  addButtonSlot,
  getClassFieldKey = (i) => `shareClass.${i}.class`,
  getTotalSharesFieldKey = (i) => `shareClass.${i}.numberOfShares`,
  changedColor,
  getIsClassChanged,
  getIsTotalSharesChanged,
}: Props<T>) => {
  const isMobile = useIsMobile();

  const usedClassValues = useMemo(
    () => new Set((shareClasses ?? []).map((c) => c?.class).filter((v): v is string => Boolean(v))),
    [shareClasses],
  );

  const { keys, removeKeyAt } = useStableKeys(shareClasses.length, 'share-class');
  const { exitingIndex, requestRemove } = useDeferredRemove((index) => {
    removeKeyAt(index);
    onRemoveShareClass(index);
  });

  const addShareClassButton = (
    <Button size="md" onClick={onAddShareClass} disabled={disabled}>
      <AddIcon />
      Add new Share Class
    </Button>
  );

  return (
    <>
      {canAddMore && addButtonSlot && createPortal(addShareClassButton, addButtonSlot)}

      {shareClasses.map((el, index) => {
        const availableClassOptions = selectClassOptions.filter(
          (opt) => opt.value === el.class || !usedClassValues.has(opt.value),
        );

        const showClassError = Boolean(fieldErrors[getClassFieldKey(index)]) && isStepValidated;
        const showTotalSharesError =
          Boolean(fieldErrors[getTotalSharesFieldKey(index)]) && isStepValidated;

        const classAllocated = (el.shareholders ?? []).reduce(
          (sum, s) => sum + (Number(s?.numberOfShares) || 0),
          0,
        );

        const classTotal = Number(el?.numberOfShares ?? 0);
        const percentText =
          classTotal > 0 ? `${((classAllocated / classTotal) * 100).toFixed(2)}%` : '-';

        const classUnderAllocated = classTotal > 0 && classAllocated < classTotal;
        const classOverAllocated = classTotal > 0 && classAllocated > classTotal;

        const classChangedColor = getIsClassChanged?.(index) ? changedColor : undefined;
        const sharesChangedColor = getIsTotalSharesChanged?.(index) ? changedColor : undefined;

        return (
          <ShareClassCard
            key={keys[index]}
            $exiting={exitingIndex === index}
            sx={{ overflowX: isMobile ? 'auto' : 'visible' }}
          >
            <Stack flexDirection="column" gap={`${theme.spacing[16]}px`}>
              <Typography variant="h6">{`#${index + 1} Share Class`}</Typography>

              <ShareClassFields sx={{ minWidth: isMobile ? 720 : 'unset' }}>
                <ChangedOutline $color={classChangedColor}>
                  <Select
                    size="lg"
                    label="Class"
                    required
                    placeholder="Select share class"
                    options={[...availableClassOptions]}
                    value={el.class ?? null}
                    onChange={(value) => onClassChange(index, value)}
                    disabled={disabled}
                    state={showClassError ? 'destructive' : 'default'}
                    helperText={showClassError ? 'Required field' : ''}
                  />
                </ChangedOutline>

                <ChangedOutline $color={sharesChangedColor}>
                  <Input
                    size="lg"
                    label="Total shares"
                    aria-label="Total shares"
                    required
                    tooltip={TOTAL_SHARES_TOOLTIP}
                    data-testid={`total-shares-input-${index}`}
                    value={
                      el.numberOfShares == null ? '' : formatWithoutFraction(el.numberOfShares)
                    }
                    onChange={(e) => onTotalSharesChange(index, e.target.value)}
                    placeholder="Enter total number of shares"
                    maxLength={100}
                    disabled={disabled}
                    state={showTotalSharesError ? 'destructive' : 'default'}
                    helperText={showTotalSharesError ? 'Required field' : ''}
                  />
                </ChangedOutline>

                <IconButton
                  aria-label="Remove share class"
                  variant="outline"
                  color="destructive"
                  size="sm"
                  disabled={
                    disabled || disableRemove || shareClasses.length === 1 || exitingIndex !== null
                  }
                  onClick={() => requestRemove(index)}
                >
                  <TrashIcon />
                </IconButton>
              </ShareClassFields>

              {renderShareholders?.(el, index)}

              {!!el.shareholders?.length && (
                <>
                  {classUnderAllocated && (
                    <Alert
                      size="md"
                      variant="warning"
                      title={UNDER_ALLOCATED_TITLE}
                      description={UNDER_ALLOCATED_DETAIL}
                    />
                  )}

                  {classOverAllocated && (
                    <Alert size="md" variant="error" description={OVER_ALLOCATED} />
                  )}

                  <Separator />

                  <AllocationSummary>
                    <Stack alignItems="center" gap={`${theme.spacing[8]}px`}>
                      <Typography variant="body2">Aggregated figures for this class:</Typography>
                      <Tooltip side="top" title={AGGREGATED_TOOLTIP}>
                        <Box display="inline-flex">
                          <InfoIcon width={ICON_SIZE} height={ICON_SIZE} />
                        </Box>
                      </Tooltip>
                    </Stack>

                    <Stack alignItems="center" gap={`${theme.spacing[8]}px`}>
                      <Typography variant="body2" color={theme.colors.text.secondary}>
                        Number of Shares:
                      </Typography>
                      <Typography variant="body2">
                        {formatWithoutFraction(classAllocated)}
                      </Typography>
                    </Stack>

                    <Stack alignItems="center" gap={`${theme.spacing[8]}px`}>
                      <Typography variant="body2" color={theme.colors.text.secondary}>
                        % of Total:
                      </Typography>
                      <Typography variant="body2">{percentText}</Typography>
                    </Stack>
                  </AllocationSummary>
                </>
              )}
            </Stack>
          </ShareClassCard>
        );
      })}

      {canAddMore && !addButtonSlot && <Stack alignItems="flex-start">{addShareClassButton}</Stack>}
    </>
  );
};
